import params from "../../params.json";
import { socialMediaLinks } from "@consts/socialMediaLinks.js";

const projectCode = process.env.REACT_APP_PROJECT_CODE || "test";

const {
  SERVICE_URL: serviceUrl,
  SOURCE_URL: sourceUrl,
  LK_URL: lkUrl,
  BACKOFFICE_URL: backOfficeUrl,
  EMAIL_URL: emailUrl,
  REQUEST_PARAMS: requestParams,
} = params[projectCode];

if (!lkUrl || !backOfficeUrl || !sourceUrl) {
  throw new Error("Invalid env params");
}

const headers = {
  "Content-type": "application/json; charset=utf-8",
};

const requestBody = {
  ...requestParams,
};

const createSession = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(`${serviceUrl}/Session`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          SourceUrl:
            requestParams.Source === "PDL" ? document.location.href : sourceUrl,
        }),
      });

      const data = await response.json();

      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const getInitialCalcData = () => {
  return new Promise(async (resolve, reject) => {
    const sessionID = localStorage.getItem("guid");
    try {
      const response = await fetch(
        `${serviceUrl}/commonApi/pdlParams/${sessionID}`,
      );
      const data = await response.json();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const getCalculatedDataByParams = ({ Amount, Term }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const body = {
        Amount,
        Term,
        ...requestBody,
      };
      const response = await fetch(`${serviceUrl}/commonApi/pdlParams`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          ...headers,
          SessionId: localStorage.getItem("guid"),
        },
      });
      const data = await response.json();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const getRedirectToServiceLinkByParams = ({ Amount, Term }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const body = {
        Amount,
        Term,
        ...requestBody,
      };
      const response = await fetch(`${serviceUrl}/commonApi/preOrder`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          ...headers,
          SessionId: localStorage.getItem("guid"),
        },
      });
      const data = await response.json();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const sendMail = ({ subject, text, emailTo, pinnedFiles }) => {
  return new Promise(async (resolve, reject) => {
    const payload = {
      subject,
      text,
      emailTo: emailTo ?? socialMediaLinks.email,
      pinnedFiles,
    };
    try {
      const response = await fetch(`${emailUrl}/feedback/email`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          ...headers,
        },
      });
      const data = await response.text();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const sendFeedback = async (
  { phoneNumber, name, organization },
  siteVersion,
  email,
) => {
  const messengers = {
    client: {
      subject: `Заявка на обратный звонок от клиента ${phoneNumber}`,
      text: `Заявка на обратный звонок от клиента ${phoneNumber}`,
    },

    business: {
      subject: `Заявка на подключение партнера ${organization}`,
      text: `Заявка на подключение партнера ${organization}, контакт ${name} ${phoneNumber}`,
    },
  };

  return await sendMail({
    ...messengers[siteVersion],
    emailTo: email,
  });
};

const sendComplaint = ({ name, surname }, pinnedFiles, email) => {
  const text = `Estimado ${name} ${surname} su recurso se estudiará en un plazo de treinta días. Espere una respuesta a su correo electrónico.`;
  const subject = "Reclamación sobre el producto servicio (DoctorSol)";
  const getParams = () => ({
    subject,
    text,
    emailTo: email,
    pinnedFiles,
  });

  return new Promise(async (resolve, reject) => {
    try {
      const data = sendMail(getParams());
      resolve(data);
    } catch (e) {
      reject(e);
    }
  });
};

const defaultSessionHeaders = () => ({
  SessionId: localStorage.getItem("guid"),
});

const buildQueryParams = (params) => {
  const query = new URLSearchParams();

  if (params.brandName) query.append("brandName", params.brandName);
  if (params.culture) query.append("culture", params.culture);
  if (params.limit != null) query.append("limit", params.limit);
  if (params.offset != null) query.append("offset", params.offset);

  if (Array.isArray(params.productCategories)) {
    params.productCategories.forEach((cat) =>
      query.append("productCategories", cat),
    );
  }

  if (Array.isArray(params.regions)) {
    params.regions.forEach((region) => query.append("regions", region));
  }

  return query.toString();
};

const getPartnersData = (filters = {}) => {
  return new Promise(async (resolve, reject) => {
    try {
      const query = buildQueryParams(filters);

      const response = await fetch(`${serviceUrl}/partners?${query}`, {
        method: "GET",
        headers: defaultSessionHeaders(),
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const getPartnerById = (id, language = "uz") => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(
        `${serviceUrl}/partners/${id}?culture=${language}`,
        {
          method: "GET",
          headers: defaultSessionHeaders(),
          credentials: "include",
        },
      );
      const data = await response.json();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

const getPartnerCategories = (language = "uz") => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(
        `${serviceUrl}/partners/categories?culture=${language}`,
        {
          method: "GET",
          headers: defaultSessionHeaders(),
          credentials: "include",
        },
      );
      const data = await response.json();
      if (response.ok) resolve(data);
      else reject();
    } catch (e) {
      reject(e);
    }
  });
};

export {
  serviceUrl,
  sourceUrl,
  lkUrl,
  backOfficeUrl,
  getInitialCalcData,
  getCalculatedDataByParams,
  getRedirectToServiceLinkByParams,
  sendFeedback,
  sendComplaint,
  createSession,
  getPartnersData,
  getPartnerById,
  getPartnerCategories,
};
